import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import BackToTop from "@/components/feature/BackToTop";
import Breadcrumbs from "@/components/feature/Breadcrumbs";
import JsonLd from "@/components/feature/JsonLd";
import { useSeo } from "@/hooks/useSeo";
import { testimonials } from "@/mocks/testimonials";

const principles = [
  {
    icon: "ri-book-open-line",
    title: "来源优先",
    desc: "每条线索尽量标注来源、时间与地点。无法核实的说法会明确说明，而不是包装成事实。",
  },
  {
    icon: "ri-user-voice-line",
    title: "尊重当事人",
    desc: "真实案件中的当事人与家属不是素材。我们隐去不必要的私人信息，也不做没有依据的指认。",
  },
  {
    icon: "ri-scales-3-line",
    title: "不下结论",
    desc: "悬案的价值在于过程。我们把各种可能性摆在一起，保留它们之间的缝隙，交给读者判断。",
  },
  {
    icon: "ri-heart-3-line",
    title: "不制造恐惧",
    desc: "部分题材天然沉重，但我们拒绝用血腥、惊吓和夸张标题换取点击。好奇归好奇，安全第一。",
  },
];

const milestones = [
  {
    year: "缘起",
    title: "从一场深夜争论开始",
    desc: "我们最初只是一群喜欢翻旧案的普通读者。因为受不了市面上把悬案写成纯惊悚故事，决定自己动手整理时间线。",
  },
  {
    year: "整理",
    title: "把碎片拼成卷宗",
    desc: "从古籍、公开报道到读者口述，我们开始按题材分类归档，并为每篇档案补上可追溯的线索链条。",
  },
  {
    year: "共建",
    title: "让读者成为合著者",
    desc: "投稿与勘误逐渐成为内容的重要来源。很多最精彩的细节，正是读者在回信里补上的。",
  },
  {
    year: "现在",
    title: "继续把档案写长",
    desc: "我们不追求更新速度，而希望每一篇都经得起重读。哪怕只有一个人因此睡得更晚一点，也算没白写。",
  },
];

export default function About() {
  useSeo({
    title: "关于我们｜猎奇吃瓜内容团队与选题原则",
    description:
      "猎奇吃瓜是一个专注未解之谜、灵异杂谈与都市传说的中文内容团队。了解我们的品牌定位、发展理念、内容原则与联系方式。",
    keywords: "关于猎奇吃瓜,内容原则,媒体团队,未解之谜,灵异杂谈,悬疑档案",
    canonical: "/about",
    type: "website",
  });

  return (
    <div className="flex min-h-screen flex-col bg-background-950">
      <SiteHeader />
      <main className="flex-1">
        <section className="relative overflow-hidden border-b border-background-800">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Abstract%20dark%20editorial%20scene%20of%20stacked%20aged%20archival%20folders%20and%20a%20brass%20magnifying%20glass%20on%20a%20dark%20walnut%20desk%2C%20warm%20amber%20desk%20lamp%20glow%20against%20deep%20charcoal%20shadows%2C%20moody%20cinematic%20photograph%20with%20soft%20grain%2C%20no%20people%20no%20text&width=1600&height=900&seq=lieqi-about-hero-01&orientation=landscape"
              alt="猎奇吃瓜 编辑室档案桌"
              title="关于猎奇吃瓜 - 档案编辑室"
              className="h-full w-full object-cover object-top"
            />
          </div>
          <div className="absolute inset-0 bg-gradient-to-b from-background-950/85 via-background-950/80 to-background-950/95" />
          <div className="relative mx-auto w-full max-w-content px-4 py-16 md:px-6 md:py-24">
            <Breadcrumbs items={[{ label: "首页", to: "/" }, { label: "关于我们" }]} />
            <p className="mt-5 text-xs tracking-[0.3em] text-primary-400">ABOUT THE ARCHIVE</p>
            <h1 className="mt-3 max-w-3xl font-heading text-3xl font-black leading-tight text-foreground-50 md:text-5xl">
              一群愿意把悬案读完的人
            </h1>
            <p className="mt-5 max-w-2xl text-sm leading-relaxed text-foreground-300 md:text-base">
              猎奇吃瓜不是一家媒体机构，而是一个由读者、编辑与记录者共同维护的内容档案。我们相信，面对未解之谜最好的态度，是耐心、克制，以及把话讲清楚。
            </p>
          </div>
        </section>

        <section className="mx-auto w-full max-w-content px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2">
            <div>
              <p className="text-xs tracking-[0.3em] text-accent-300">BRAND</p>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-50 md:text-3xl">我们是谁</h2>
              <div className="mt-5 space-y-4 text-sm leading-loose text-foreground-400 md:text-base">
                <p>
                  猎奇吃瓜起源于一个很小的兴趣小组。我们喜欢夜里翻旧报纸的电子档案，也喜欢听朋友讲那些「说不清」的经历，于是决定把这些内容系统地整理下来。
                </p>
                <p>
                  名字里的「吃瓜」其实是一种自嘲——面对悬案，我们和读者一样，都是坐在旁边看戏、又忍不住想追问下去的人。只是我们愿意多做一步：把时间线排清楚，把不同的说法都写出来。
                </p>
              </div>
              <div className="mt-7 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="rounded-lg border border-background-800 bg-background-900 p-4">
                  <p className="font-heading text-2xl font-bold text-primary-300">6</p>
                  <p className="mt-1 text-xs text-foreground-500">长期内容栏目</p>
                </div>
                <div className="rounded-lg border border-background-800 bg-background-900 p-4">
                  <p className="font-heading text-2xl font-bold text-primary-300">2-3</p>
                  <p className="mt-1 text-xs text-foreground-500">每周更新篇幅</p>
                </div>
                <div className="rounded-lg border border-background-800 bg-background-900 p-4">
                  <p className="font-heading text-2xl font-bold text-primary-300">100%</p>
                  <p className="mt-1 text-xs text-foreground-500">读者共建投稿</p>
                </div>
              </div>
            </div>
            <div className="overflow-hidden rounded-lg border border-background-800">
              <img
                src="https://readdy.ai/api/search-image?query=Close%20up%20of%20hands%20sorting%20old%20yellowed%20case%20files%20and%20photographs%20on%20a%20dark%20wooden%20table%2C%20warm%20amber%20lamp%20lighting%20from%20one%20side%2C%20deep%20charcoal%20shadows%2C%20moody%20documentary%20photography%20with%20film%20grain%2C%20no%20faces%20no%20text&width=1200&height=900&seq=lieqi-about-brand-01&orientation=landscape"
                alt="整理旧案卷宗的档案桌面"
                title="猎奇吃瓜 档案整理"
                className="h-72 w-full object-cover object-top md:h-96"
              />
            </div>
          </div>
        </section>

        <section className="border-y border-background-800 bg-background-900/60">
          <div className="mx-auto w-full max-w-content px-4 py-14 md:px-6 md:py-20">
            <p className="text-xs tracking-[0.3em] text-primary-400">OUR POSITION</p>
            <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-50 md:text-3xl">网站定位与内容原则</h2>
            <p className="mt-4 max-w-3xl text-sm leading-relaxed text-foreground-400 md:text-base">
              我们不想成为「恐怖故事库」，也不想假装自己掌握真相。猎奇吃瓜的定位是：用可读的方式，把悬案与异闻整理成值得被认真对待的记录。
            </p>

            <div className="mt-8 grid grid-cols-1 gap-5 sm:grid-cols-2">
              {principles.map((item) => (
                <div key={item.title} className="flex gap-4 rounded-lg border border-background-800 bg-background-950 p-5">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-secondary-500/40 bg-secondary-500/10 text-secondary-300">
                    <i className={`${item.icon} text-xl`} />
                  </span>
                  <div>
                    <h3 className="font-heading text-base font-semibold text-foreground-50">{item.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-foreground-400">{item.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-content px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-[minmax(0,320px)_1fr]">
            <div className="lg:sticky lg:top-28 lg:self-start">
              <p className="text-xs tracking-[0.3em] text-accent-300">TIMELINE</p>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-50 md:text-3xl">发展理念</h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-400">
                从兴趣小组到内容档案，我们一直在做同一件事：让猎奇题材被更认真地对待。
              </p>
            </div>
            <ol className="relative space-y-6 border-l border-background-800 pl-6">
              {milestones.map((item) => (
                <li key={item.title} className="relative">
                  <span className="absolute -left-[1.9rem] top-1.5 flex h-3 w-3 items-center justify-center rounded-full border-2 border-primary-500 bg-background-950" />
                  <p className="text-xs tracking-[0.25em] text-primary-400">{item.year}</p>
                  <h3 className="mt-2 font-heading text-lg font-semibold text-foreground-50">{item.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-foreground-400">{item.desc}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        <section className="border-y border-background-800 bg-background-900/60">
          <div className="mx-auto w-full max-w-content px-4 py-14 md:px-6 md:py-20">
            <div className="text-center">
              <p className="text-xs tracking-[0.3em] text-primary-400">READER VOICES</p>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-50 md:text-3xl">读者口碑</h2>
              <p className="mx-auto mt-3 max-w-2xl text-sm leading-relaxed text-foreground-400">
                这几条留言来自订阅读者的回信。我们没有为它们加修饰，也保留了原始的口气。
              </p>
            </div>
            <div className="mt-10 grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-3">
              {testimonials.slice(0, 3).map((item, index) => (
                <figure
                  key={item.name}
                  className="flex h-full flex-col rounded-lg border border-background-800 bg-background-950 p-5"
                >
                  <i className="ri-double-quotes-l text-2xl text-primary-500/60" />
                  <blockquote className="mt-3 flex-1 text-sm leading-relaxed text-foreground-300">
                    {item.content}
                  </blockquote>
                  <figcaption className="mt-5 flex items-center gap-3 border-t border-background-800 pt-4">
                    <span
                      className={`flex h-10 w-10 items-center justify-center rounded-full font-heading text-sm font-bold ${
                        index % 2 === 0
                          ? "bg-primary-500/15 text-primary-300"
                          : "bg-secondary-500/15 text-secondary-300"
                      }`}
                    >
                      {item.name.slice(0, 1)}
                    </span>
                    <span className="flex flex-col">
                      <span className="text-sm font-medium text-foreground-100">{item.name}</span>
                      <span className="text-xs text-foreground-500">{item.role}</span>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="mx-auto w-full max-w-content px-4 py-14 md:px-6 md:py-20">
          <div className="grid grid-cols-1 items-center gap-8 rounded-lg border border-background-800 bg-background-900 p-6 md:p-10 lg:grid-cols-2">
            <div>
              <p className="text-xs tracking-[0.3em] text-accent-300">CONTACT</p>
              <h2 className="mt-3 font-heading text-2xl font-bold text-foreground-50 md:text-3xl">联系我们</h2>
              <p className="mt-4 text-sm leading-relaxed text-foreground-400 md:text-base">
                线索提供、内容勘误、转载与商务合作，都可以通过邮件与我们联系。我们通常在三个工作日内回复。
              </p>
              <div className="mt-6 space-y-3 text-sm text-foreground-300">
                <p className="flex items-center gap-3">
                  <i className="ri-mail-line text-primary-400" /> contact@lieqichigua.com
                </p>
                <p className="flex items-center gap-3">
                  <i className="ri-chat-3-line text-primary-400" /> 投稿邮箱：submit@lieqichigua.com
                </p>
                <p className="flex items-center gap-3">
                  <i className="ri-time-line text-primary-400" /> 回复时间：工作日 09:00 - 18:00
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-3">
              <Link
                to="/archives"
                className="flex items-center justify-between whitespace-nowrap rounded-md bg-primary-500 px-5 py-3.5 text-sm font-semibold text-background-950 transition-colors hover:bg-primary-400"
              >
                进入档案库 <i className="ri-arrow-right-line" />
              </Link>
              <Link
                to="/faq"
                className="flex items-center justify-between whitespace-nowrap rounded-md border border-background-700 px-5 py-3.5 text-sm text-foreground-200 transition-colors hover:border-primary-400 hover:text-primary-300"
              >
                查看常见问题 <i className="ri-arrow-right-line" />
              </Link>
              <p className="mt-2 text-xs leading-relaxed text-foreground-500">
                温馨提示：本站内容仅供阅读与讨论，请勿据此前往传闻地点探访，现实安全永远排在好奇心之前。
              </p>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
      <BackToTop />

      <JsonLd
        id="about-schema"
        data={{
          "@context": "https://schema.org",
          "@type": "AboutPage",
          name: "关于猎奇吃瓜",
          description: "猎奇吃瓜的内容定位、发展理念、内容原则与联系方式。",
          url: "https://dioxy.cn/about",
          inLanguage: "zh-CN",
          mainEntity: {
            "@type": "Organization",
            name: "猎奇吃瓜",
            url: "https://dioxy.cn/",
            email: "contact@lieqichigua.com",
            description: "专注未解之谜、灵异杂谈、都市传说、超自然现象、悬疑档案与暗网传闻的中文内容档案。",
          },
        }}
      />
      <JsonLd
        id="about-breadcrumb"
        data={{
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "首页", item: "https://dioxy.cn/" },
            { "@type": "ListItem", position: 2, name: "关于我们", item: "https://dioxy.cn/about" },
          ],
        }}
      />
    </div>
  );
}